import {
  AzureOnboardingScopeRef,
  AzureScope,
} from '../../../services/azure/azureOnboarding/azureOnboarding.model';
import { ILookup } from '../../../services/common/common.model';

export const statusDisplay: {
  [key: string]: { label: string; color: string; tooltip: string };
} = {
  AwaitingConsent: {
    label: 'Awaiting client consent',
    color: 'default',
    tooltip: "The client hasn't opened the link and granted consent yet.",
  },
  PendingRoleAssignment: {
    label: 'Waiting for Azure access',
    color: 'gold',
    tooltip:
      'Consent granted, but no active billing account or subscription yet. Either none is visible, or all are switched off.',
  },
  PendingPropagation: {
    label: 'Finishing setup',
    color: 'processing',
    tooltip:
      "Scopes are known, but none has answered yet. Roles may still be propagating, may be missing, or (for CSP) the partner hasn't turned on cost visibility.",
  },
  Connected: {
    label: 'Connected',
    color: 'success',
    tooltip: 'Data collection is running.',
  },
  Error: {
    label: 'Error',
    color: 'error',
    tooltip: 'Verification failed unexpectedly - see the server log.',
  },
};

// Client-managed connections (the client's own app registration) have no consent link, so
// the AwaitingConsent/PendingRoleAssignment copy - written for the consent flow - doesn't
// apply. Override just those tooltips/labels; every other status (and the commercial/default
// path) is unchanged.
const clientManagedStatusOverrides: {
  [key: string]: { label?: string; tooltip: string };
} = {
  AwaitingConsent: {
    label: 'Not connected',
    tooltip:
      "No Azure tenant is set for this connection. Reconnect it with Connect with client's own app.",
  },
  PendingRoleAssignment: {
    tooltip:
      "Connected to the client's app, but no active billing account or subscription yet. Check the roles granted to the app in the client's tenant, or whether all scopes are switched off.",
  },
};

export const getStatusDisplay = (status: string, clientManaged = false) => {
  const base = statusDisplay[status] || { label: status, color: 'default', tooltip: '' };
  const override = clientManaged ? clientManagedStatusOverrides[status] : undefined;
  return override ? { ...base, ...override } : base;
};

export const scopeStatusColor: { [key: string]: string } = {
  Connected: 'success',
  Pending: 'processing',
  Failed: 'error',
  NotTested: 'default',
};

// scope_type is the ARM path segment in front of the scope id ('subscriptions',
// 'providers/Microsoft.Billing/billingAccounts', or any segment an admin adds). Like the
// backend's isScopeType, compare case-insensitively and ignore surrounding slashes. The
// pre-migration names ('Subscription', 'BillingAccount') are accepted too.
const normalizeScopeType = (type: string | null | undefined) =>
  (type ?? '').trim().replace(/^\/+|\/+$/g, '').toLowerCase();

const SUBSCRIPTION_TYPES = ['subscriptions', 'subscription'];
const BILLING_ACCOUNT_TYPES = ['providers/microsoft.billing/billingaccounts', 'billingaccount'];

export const isSubscription = (scope: { scope_type: string }) =>
  SUBSCRIPTION_TYPES.includes(normalizeScopeType(scope.scope_type));

export const isBillingAccount = (scope: { scope_type: string }) =>
  BILLING_ACCOUNT_TYPES.includes(normalizeScopeType(scope.scope_type));

// Matches the backend: CSP = a Subscription whose quotaId starts with "CSP".
export const isCspScope = (scope: { scope_type: string; agreement_type?: string | null }) =>
  isSubscription(scope) && (scope.agreement_type ?? '').toUpperCase().startsWith('CSP');

export const scopeTypeLabel = (scopeType: string) => {
  if (isSubscription({ scope_type: scopeType })) return 'Subscription';
  if (isBillingAccount({ scope_type: scopeType })) return 'Billing account';
  return scopeType;
};

export const agreementLabel = (agreementType: string | null) => {
  switch (agreementType) {
    case 'MicrosoftCustomerAgreement':
      return 'MCA';
    case 'EnterpriseAgreement':
      return 'EA';
    default:
      return agreementType ?? '-';
  }
};

export const clientLink = (linkToken: string) =>
  `${window.location.origin}/connect-azure/${linkToken}`;

export const scopeName = (ref: AzureOnboardingScopeRef, scopes: AzureScope[]) =>
  scopes.find((s) => s.id === ref.id)?.display_name || ref.scope_id;

export const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '–';

// The request interceptor already toasts these; this is for showing them inline as well.
export const responseErrorText = (err: any): string => {
  const errors = err?.response?.data?.body?.errors;
  if (Array.isArray(errors)) return errors.join(' ');
  return errors || 'The request failed.';
};

// How a client-managed connection's secret expiry is shown: orange within 30 days, red once past.
export const secretExpiryDisplay = (
  expires: string | null,
  today: Date = new Date()
): { label: string; color?: 'orange' | 'red' } | null => {
  if (!expires) return null;
  const [y, m, d] = expires.split('-').map(Number);
  const expiry = Date.UTC(y, m - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((expiry - now) / 86400000);
  if (days < 0) return { label: `Expired ${expires}`, color: 'red' };
  if (days === 0) return { label: `${expires} (today)`, color: 'orange' };
  if (days === 1) return { label: `${expires} (1 day)`, color: 'orange' };
  if (days <= 30) return { label: `${expires} (${days} days)`, color: 'orange' };
  return { label: expires };
};

// Looks a lookup id up by id in a primary list first, then a secondary (fallback) list -
// e.g. the modal's own scoped lookups, then the page's global filter lookups. '–' when
// not found in either (or no id given).
export const lookupName = (
  id: number | null | undefined,
  primary?: ILookup[] | null,
  secondary?: ILookup[] | null
): string => {
  if (id === null || id === undefined) return '–';
  const found = (primary ?? []).find((o) => o.id === id) ?? (secondary ?? []).find((o) => o.id === id);
  return found?.name ?? '–';
};

export const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for browsers that block clipboard API
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
};
